/**
 * Diagram drawing guidance adapted from Agents365-ai/tldraw-skill for Illumi's
 * live board tools (createGeoShape, createArrow, etc.) — not .tldr file export.
 * @see https://github.com/Agents365-ai/tldraw-skill
 */

export const BOARD_GRID_SNAP = 10;

/** Clear gap between bottoms and tops of stacked boxes (not between y origins). */
export const BOARD_STACK_CLEAR_GAP = {
	simple: 120,
	medium: 160,
	complex: 200,
} as const;

export const BOARD_DIAGRAM_SPACING = {
	simple: {
		horizontal: 240,
		vertical: 200,
		maxNodes: 5,
		stackGap: BOARD_STACK_CLEAR_GAP.simple,
	},
	medium: {
		horizontal: 300,
		vertical: 240,
		maxNodes: 10,
		stackGap: BOARD_STACK_CLEAR_GAP.medium,
	},
	complex: {
		horizontal: 360,
		vertical: 280,
		maxNodes: Number.POSITIVE_INFINITY,
		stackGap: BOARD_STACK_CLEAR_GAP.complex,
	},
} as const;

const LABEL_METRICS = {
	s: { charWidth: 11, lineHeight: 18 },
	m: { charWidth: 15, lineHeight: 28 },
	l: { charWidth: 22, lineHeight: 40 },
	xl: { charWidth: 32, lineHeight: 56 },
} as const;

export type BoardStrokeSize = keyof typeof LABEL_METRICS;

export function getDiagramSpacing(nodeCount: number) {
	if (nodeCount <= BOARD_DIAGRAM_SPACING.simple.maxNodes) {
		return BOARD_DIAGRAM_SPACING.simple;
	}

	if (nodeCount <= BOARD_DIAGRAM_SPACING.medium.maxNodes) {
		return BOARD_DIAGRAM_SPACING.medium;
	}

	return BOARD_DIAGRAM_SPACING.complex;
}

/** Estimate geo box w/h so labels do not auto-grow and collide. */
export function estimateGeoLabelSize(
	text: string,
	size: BoardStrokeSize = "m",
) {
	const padding = 20;
	const growPad = 24;
	const lines = text.split("\n").filter((line) => line.length > 0);
	const longestLine = Math.max(1, ...lines.map((line) => line.length));
	const lineCount = Math.max(1, lines.length);
	const { charWidth, lineHeight } = LABEL_METRICS[size];
	const rawW = longestLine * charWidth + padding * 2;
	const rawH = lineCount * lineHeight + padding * 2 + growPad;

	return {
		w: Math.ceil(rawW / BOARD_GRID_SNAP) * BOARD_GRID_SNAP,
		h: Math.ceil(rawH / BOARD_GRID_SNAP) * BOARD_GRID_SNAP,
	};
}

/**
 * Y of the next stacked box: previous top + estimated height + clear gap.
 * Use estimated (or larger) height — never assume the small h you pass stays final.
 */
export function nextStackedBoxY({
	previousY,
	label,
	requestedH,
	nodeCount,
}: {
	previousY: number;
	label: string;
	requestedH?: number;
	nodeCount: number;
}) {
	const estimated = estimateGeoLabelSize(label);
	const height = Math.max(requestedH ?? 0, estimated.h, 70);
	const { stackGap } = getDiagramSpacing(nodeCount);
	return snapToBoardGrid(previousY + height + stackGap);
}

export function snapToBoardGrid(value: number) {
	return Math.round(value / BOARD_GRID_SNAP) * BOARD_GRID_SNAP;
}

export const BOARD_SEMANTIC_COLORS = {
	client: "blue",
	service: "blue",
	database: "green",
	cache: "yellow",
	queue: "orange",
	gateway: "violet",
	auth: "violet",
	external: "red",
	decision: "yellow",
	error: "red",
	warning: "light-red",
	neutral: "grey",
	annotation: "yellow",
} as const;

export function buildBoardDrawingGuideLines(): string[] {
	return [
		"Diagram triggers: use drawing tools when the user asks for a diagram/flowchart/architecture sketch, or when explaining 3+ components, pipelines, or relationships. Skip for simple lists or quick Q&A.",
		"Adjust existing content (critical): when the user asks to align, move, resize, restyle, fix overlaps, or tweak a diagram — mutate EXISTING shapes (alignShapes, moveShape, updateGeoShape, updateDrawShape, distributeShapes, resizeShapes) via runBoardPlan. Never delete all shapes and recreate unless the user explicitly asks to redraw from scratch.",
		"Diagram workflow: (1) findEmptySpace once for the whole composition (request generous size, e.g. 1100×1400 for a layered schema); (2) size each box for its label FIRST (use estimated final h, not the tiny h you hope for); (3) place with stackGap clear space using that estimated height; (4) runBoardPlan with creates AND arrows using $N refs to LAYER boxes only; (5) inspectDiagram; (6) if overlapCount > 0, redistribute with LARGE moves then inspect again; (7) only then summarize.",
		"Titles / banners: create a title geo as decoration only — NEVER bind arrows from/to the title shape. Wire arrows between architecture nodes only (layers, DBs, APIs, etc.).",
		"Illustration / picture sketches (critical — animals, icons, robots, simple scenes): DEFAULT to geo + line PRIMITIVES. Do NOT use createDrawShape freehand for these — current models produce sparse broken strokes. Build a recognizable composition from ellipses/ovals (heads, bodies, eyes), triangles (ears, noses, roofs), rectangles (torsos, legs as blocks), clouds/stars/hearts when fitting, and createLineShape for whiskers, tails, limbs, antennae, ground lines. Many plan steps are fine (15–35+). Result must read as what the user asked for (a cat looks like a cat), not abstract scraps.",
		"Primitive sketch workflow: (1) findEmptySpace once for the whole drawing (~700×700+); (2) place a large body/anchor geo first; (3) add head/ears/limbs with RELATIVE offsets so parts TOUCH or slightly overlap the body (no floating ears); (4) add details with lines (2–4 points OK for straight whiskers/legs) or small geos (eyes as ellipses); (5) optional labels with geo text or createTextShape only if needed; (6) setShapeZOrder (large shapes back, details front); (7) inspectDiagram for box overlaps among geos; (8) exportViewportImage with shapeIds = that plan’s touchedShapeIds (tight crop — never full page) and if unrecognized, ADD/MOVE primitives then re-export. Quality over brevity.",
		"Primitive recipes — Cat: oval body + circle/ellipse head attached at top; two triangles on head as ears (touching); two small ellipses for eyes; createLineShape whiskers (left/right of head) and a curved/polyline tail from the body. Robot: rectangle body + square/ellipse head touching top; two thin rectangles or lines as arms/legs attached at sides/bottom; circles for eyes. Tree: brown rectangle trunk + green ellipses or triangles for canopy overlapping the trunk top. House: rectangle + triangle roof touching top + small rectangle door. Prefer solid/semi fills and distinct colors so parts read clearly.",
		"createDrawShape: LAST RESORT only — underlines, doodle marks, or when the user explicitly demands pencil freehand. Never the default for “draw a cat/robot/icon”. Prefer createGeoShape + createLineShape.",
		"Spacing (critical): gaps are CLEAR SPACE between box bottoms and tops — not distance between y origins. Vertical stack: nextY = prevY + finalBoxH + stackGap. stackGap ≥ 120px (≤5 nodes), ≥ 160px (6–10), ≥ 200px (>10). Two-line geos often finish ~120–150px tall even if you pass h:90 — plan nextY with ≥150h + stackGap. Never repair overlaps with 10–30px nudges; shift by full stackGap or more until boxes stop colliding and labels are readable.",
		"Box sizing: geo shapes AUTO-GROW when text overflows. Multiline labels (e.g. Presentation Layer + Compose) often become ~120–150px tall. Always set w/h ≥ estimated size (≈15px/char + 32px pad, ≈28px/line) and prefer slightly larger. Single-line: h ≥ 70. Two-line: h ≥ 120. Three-line: h ≥ 150. For illustration geos without long text, pick sizes that match the part (head ~80–140px, body larger).",
		"Horizontal layouts: leave ≥ 240px between left edges for simple diagrams, and ≥ 80px CLEAR gap between sibling boxes at the same y (Local/Remote).",
		"runBoardPlan step refs: $0 is shapeId from steps[0], $1 from steps[1]. Put creates + arrows in ONE plan when possible. Never invent shapeIds; never parallel createArrow with a creates-only plan. Plans may mix creates AND mutations, or be mutations-only for adjustments. Illustration plans may use many createGeoShape / createLineShape steps — up to the plan step limit.",
		"Geo styles: pass fill (none/semi/solid), dash, size, font, align, verticalAlign, labelColor on createGeoShape / updateGeoShape when needed — do not recreate just to change style.",
		"Highlight (createHighlightShape): semi-transparent marker to emphasize regions — not for diagram nodes or picture sketches.",
		"Semantic colors: blue=clients/services, green=databases/success, orange=queues, violet=gateways/auth, yellow=decisions/caches, red=external/errors, grey=neutral. For illustrations pick clear fills (e.g. orange cat, grey robot) so silhouettes read at a glance.",
		"Geo vocabulary: rectangle=service/process/torso, ellipse/oval=DB or head/body blob, diamond=decision, hexagon=event bus, triangle=gateway/ear/roof, cloud=external API, arrow-right/left/up/down=directional markers, star/octagon/heart=emphasis icons.",
		"Arrows: bind via exact shapeIds or $N of NODE shapes only. Prefer labeled arrows for decision branches. Curved: kind=arc + bend (±40..120). Elbow: kind=elbow + elbowMidPoint 0–1. If overlappingArrowCount > 0, curve with opposite bend signs via updateArrow — do not stack straight arrows.",
		"Presets — Architecture stack: title (no arrows) → layers top→bottom with stackGap using estimated heights → data stores as a wide row with ≥80px clear horizontal gap. Flowchart: oval start/end, rectangles for steps, diamonds for decisions.",
		"Mandatory: if inspectDiagram reports overlapCount > 0 among diagram boxes (labels unreadable), redistribute with large moves/stackGap before any success reply. If overlappingArrowCount > 0, curve those arrows. Illustrations: parts should touch intentionally (that is good); only fix accidental total stacks. exportViewportImage MUST use illustration shapeIds (or this-turn session fallback) — never full-board PNGs. Always end with a short natural reply — never tools-only.",
	];
}
